import { z } from 'zod';
import { validarCPF } from './validationUtils';

// Schema base (todos os produtos)
export const itemBaseSchema = z.object({
  produto_id: z.string().uuid({ message: 'Selecione um produto válido' }),
  quantidade: z.number().min(1, { message: 'Quantidade mínima: 1' }),
  valor_unit: z.number().positive({ message: 'Valor deve ser maior que zero' }),
  plano_nome: z.string().min(1, { message: 'Informe o nome do plano/serviço' }),
});

// Schema FIBRA
export const itemFibraSchema = itemBaseSchema.extend({
  velocidade: z.string().min(1, { message: 'Informe a velocidade (ex: 100MB, 1GB)' }).optional(),
  tecnologia: z.enum(['FTTH', 'FTTC', 'HFC', ''], { message: 'Selecione a tecnologia' }).optional(),
  endereco_instalacao: z.object({
    cep: z.string().regex(/^\d{8}$/, { message: 'CEP inválido (8 dígitos)' }).optional(),
    logradouro: z.string().min(3, { message: 'Endereço obrigatório' }).optional(),
    numero: z.string().min(1, { message: 'Número obrigatório' }).optional(),
    bairro: z.string().min(2, { message: 'Bairro obrigatório' }).optional(),
    cidade: z.string().min(2, { message: 'Cidade obrigatória' }).optional(),
    uf: z.string().length(2, { message: 'UF inválido (2 letras)' }).optional(),
  }).optional(),
  agenda_data: z.string().optional(),
  agenda_tipo: z.enum(['INSTALACAO', 'PORTABILIDADE', ''], { message: 'Tipo de agendamento obrigatório' }).optional(),
});

// Schema MÓVEL base
const itemMovelBaseSchema = itemBaseSchema.extend({
  ddd: z.string().length(2, { message: 'DDD deve ter 2 dígitos' }).optional(),
  linha: z.string().optional(),
  tipo_linha: z.enum(['NOVA', 'PORTABILIDADE', 'MIGRACAO', ''], { message: 'Selecione o tipo de linha' }).optional(),
  operadora_doadora: z.string().optional(),
  cpf_titular: z.string().optional(),
});

export const itemMovelSchema = itemMovelBaseSchema.superRefine((data, ctx) => {
  // Se é portabilidade, validar campos adicionais
  if (data.tipo_linha === 'PORTABILIDADE') {
    if (!data.operadora_doadora || data.operadora_doadora.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Operadora doadora obrigatória para portabilidade',
        path: ['operadora_doadora'],
      });
    }
    if (!data.linha || data.linha.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Número da linha obrigatório para portabilidade',
        path: ['linha'],
      });
    }
    if (data.cpf_titular && !validarCPF(data.cpf_titular)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'CPF inválido',
        path: ['cpf_titular'],
      });
    }
  }
});

// Schema APARELHO
export const itemAparelhoSchema = itemBaseSchema.extend({
  marca: z.string().min(2, { message: 'Informe a marca' }).optional(),
  modelo: z.string().min(2, { message: 'Informe o modelo' }).optional(),
  forma_pagamento: z.enum(['AVISTA', 'PARCELADO', ''], { message: 'Forma de pagamento obrigatória' }).optional(),
});

// Função helper para selecionar schema
export function getSchemaByFamilia(familia: string | undefined, strict: boolean = false) {
  if (!familia) return itemBaseSchema;
  
  const familiaUpper = familia.toUpperCase();
  
  // Se strict = true, torna campos opcionais em obrigatórios
  if (strict) {
    switch (familiaUpper) {
      case 'FIBRA':
      case 'INTERNET':
        return itemFibraSchema.extend({
          velocidade: z.string().min(1, { message: 'Velocidade obrigatória' }),
          tecnologia: z.enum(['FTTH', 'FTTC', 'HFC'], { message: 'Tecnologia obrigatória' }),
          endereco_instalacao: z.object({
            cep: z.string().regex(/^\d{8}$/, { message: 'CEP obrigatório' }),
            logradouro: z.string().min(3, { message: 'Endereço obrigatório' }),
            numero: z.string().min(1, { message: 'Número obrigatório' }),
            bairro: z.string().min(2, { message: 'Bairro obrigatório' }),
            cidade: z.string().min(2, { message: 'Cidade obrigatória' }),
            uf: z.string().length(2, { message: 'UF obrigatório' }),
          }),
          agenda_data: z.string().min(1, { message: 'Data de agendamento obrigatória' }),
        });
      case 'MOVEL':
      case 'LINHA MÓVEL':
        return itemMovelBaseSchema.extend({
          ddd: z.string().length(2, { message: 'DDD obrigatório' }),
          tipo_linha: z.enum(['NOVA', 'PORTABILIDADE', 'MIGRACAO'], { message: 'Tipo de linha obrigatório' }),
        }).superRefine((data, ctx) => {
          if (data.tipo_linha === 'PORTABILIDADE') {
            if (!data.operadora_doadora || data.operadora_doadora.trim() === '') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Operadora doadora obrigatória para portabilidade',
                path: ['operadora_doadora'],
              });
            }
            if (!data.linha || data.linha.trim() === '') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Número da linha obrigatório para portabilidade',
                path: ['linha'],
              });
            }
            if (data.cpf_titular && !validarCPF(data.cpf_titular)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'CPF inválido',
                path: ['cpf_titular'],
              });
            }
          }
        });
      case 'APARELHO':
      case 'EQUIPAMENTO':
        return itemAparelhoSchema.extend({
          marca: z.string().min(2, { message: 'Marca obrigatória' }),
          modelo: z.string().min(2, { message: 'Modelo obrigatório' }),
          forma_pagamento: z.enum(['AVISTA', 'PARCELADO'], { message: 'Forma de pagamento obrigatória' }),
        });
    }
  }
  
  // Modo não-strict (opcional)
  switch (familiaUpper) {
    case 'FIBRA':
    case 'INTERNET':
      return itemFibraSchema;
    case 'MOVEL':
    case 'LINHA MÓVEL':
      return itemMovelSchema;
    case 'APARELHO':
    case 'EQUIPAMENTO':
      return itemAparelhoSchema;
    default:
      return itemBaseSchema;
  }
}
